// WP5.1a (an iPhone's first visit, iOS 27.3): ONE program for the lit standard surfaces (engine-wide, resort-agnostic).
//
// Safari compiles every distinct program text once per stage, about 1 s for a lit pipeline on an iPhone, and identical
// text is nearly free. three.js r187 writes a new program for every optional part a material has or has not: a colour
// map, an emission map, a normal map, vertex colours, a glow mask, the baked light, the room behind a window, the night
// self-light, the snow tint, the sparkle. Aurora's phone set built 25 lit fragment programs, Lumo's 23, for about 2
// families. Here every MeshStandardNodeMaterial that three would build plainly (no own colour / normal set-up of a
// system: the ground's chain hooks, people, coats) takes the SAME graph, the industry's way (an uber-shader):
//   - the colour map, the emission map (the colour map's texel: the library's only emission maps are its colour maps)
//     and the normal map are always read, through a 1x1 white picture (WHITE) where a material has none: x 1.0 is exact.
//     The normal map runs inside a uniform If on its presence (the plain normal untouched).
//   - vertex colours, the glow mask and the baked light's coordinates always reach the fragment as varyings; the vertex
//     stage writes the attribute or a constant (vertex programs stay variants: cheap), and the fragment picks the
//     varying or the exact neutral value with a per-object uniform (select: exact, whatever the interpolation).
//   - the baked light (lazy/bakedlight.js), the room (materials.js windowWarm), the night self-light (gpu/nodemat.js
//     nightFill), the snow tint (materials.js addSnowShading) and the sparkle (the look levels) run inside uniform Ifs on
//     their exact zero (performance.md "Skip work that weighs 0").
//   - each material's values are read from the material per object (material reference nodes, ONE node for every
//     material), never a fresh uniform() per material.
// Terms that only some maps have (the baked light, the rooms, the sparkle: resort data) are in every program of a visit
// or in none (FAMILY, fixed at the first build).
// Splits that stay, by design or for another package: transparent / alpha-tested / flat-shaded / double vs single
// sided (state in the program), physical (glass: its own family), a roughness map (Lumo's matlook), three's batch
// varying (WP4), the herd coats (instancing.js builds them through diffuseRGB). Receiving the sun's shadow is a value
// of the object for a shared surface (WP5.1b: shadows.js SunShadowNode, gpu/lighting.js shadowByValue).
// Checks: tests/unit/runtime/surface.test.mjs (equal WGSL for every combination); the dump (.local/wgsl) and the held
// frames (0 px) in the PR.
import * as THREE from 'three/webgpu';
import {
  Fn, If, select, float, vec2, vec3, vec4, uniform, varying, attribute, vertexColor, normalView, normalMap, materialReference,
  diffuseColor, positionGeometry, normalGeometry, modelWorldMatrix, cameraPosition, floor, max, min, pow, mix, dot, smoothstep,
  step, fract, sin, normalize, length, abs, clamp, exp, uv, positionWorld, positionView, fwidth, renderGroup, uniformTexture,
} from 'three/tsl';
import { NIGHT_LIGHT_FILL } from './nodemat.js';
import { usable } from './internals.js'; // VIS-194: three's private parts, checked at start-up

/** The neutral picture of a slot a material leaves empty: white, read as exactly 1.0 (nearest texels: a float filter's
 *  weights need not sum to exactly 1). Not Nearest both ways: three gives such a texture no sampler, another program
 *  text. 4 bytes on the GPU. */
export const WHITE = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1);
WHITE.magFilter = THREE.NearestFilter; WHITE.minFilter = THREE.NearestMipmapNearestFilter;
WHITE.name = 'surface white';
WHITE.matrixAutoUpdate = false; // its uv matrix is the identity for good: no sin/cos per draw for a missing map
WHITE.needsUpdate = true;

/** Terms of the map's data, in every surface program of the visit or in none; fixed at the first build (a change after
 *  it would give the programs built later another text). baked: lazy/bakedlight.js (a picture loaded); room and
 *  sparkle: materials.js applyLook (a look level). */
export const FAMILY = { baked: false, room: false, sparkle: false, frozen: false };
export function familyTerms(o) {
  const same = Object.entries(o).every(([k, v]) => FAMILY[k] === !!v);
  if (FAMILY.frozen && !same) { console.warn('[surface] terms changed after the first build: kept as they were', o); return false; }
  for (const [k, v] of Object.entries(o)) FAMILY[k] = !!v;
  return true;
}

/** The baked light's values (lazy/bakedlight.js sets them; the picture before the first build). */
const live = (v) => uniform(v).setGroup(renderGroup);
export const BAKED = { map: uniformTexture(WHITE), on: live(1), sky: live(1), room: live(1), falloff: live(1), glow: live(new THREE.Color()) };
/** The snow's shade tint (materials.js snowFx.tint: main.js applyTime sets it). */
export const SNOW_TINT = uniform(new THREE.Color(0, 0, 0)).setGroup(renderGroup);

// ---- what a material is to the shared graph (decided per build; also part of its program key)
const TEX_SLOT = (t) => !!t && t.isTexture === true && !t.isArrayTexture && !t.isData3DTexture && !t.isCubeTexture && !t.isVideoTexture && (t.channel ?? 0) === 0;
// a set-up of the material's own: an own property (gpu/chain.js, diffuseRGB) or a subclass's method (trees.js
// VegNodeMaterial); not ours, which Material.copy copies onto a clone as an own property
const ownSetup = (o, k) => o[k] !== THREE.MeshStandardNodeMaterial.prototype[k];
// another picture three reads on its own: such a material is a program of its own whatever it shares, so it keeps three's
// text and its texture count (Lumo's roughness-mapped library: 15 of a fragment stage's 16 sampled textures)
const EXTRA_MAPS = ['roughnessMap', 'metalnessMap', 'aoMap', 'lightMap', 'alphaMap', 'bumpMap', 'displacementMap', 'envMap', 'specularMap', 'specularIntensityMap', 'specularColorMap', 'clearcoatMap', 'sheenColorMap', 'transmissionMap', 'thicknessMap', 'iridescenceMap', 'anisotropyMap'];
/** The snow tint and sparkle of a family material (materials.js addSnowShading, addSparkle): plain own properties that
 *  Material.copy does not carry, as it never carried their hooks, so a clone looks as a clone did before. */
export const SURF_SNOW = Symbol('surfSnow'), SURF_SPARKLE = Symbol('surfSparkle');
const OUTSIDE = Object.freeze({ colour: false, normal: false, emissive: false, family: false });
// (a room added after the first build is no term of the family's programs: that material keeps its own room node)
const tagOf = (n) => (n && (n.surfGlow || (n.surfRoom && FAMILY.room) || n.surfNight) ? n : null);
/**
 * -> { colour, normal, emissive, family } for a MeshStandardNodeMaterial (null for any other material):
 *   colour   the colour stage is the shared one (no own colorNode, a plain colour map or none)
 *   normal   the normal stage is the shared one (no own normalNode, no bump map, a tangent-space RGB normal map or none)
 *   emissive the emission is the shared one (no own emissiveNode but a tagged one, no emission map or the colour map)
 *   family   the material carries the family's terms (baked light, snow tint, sparkle, room, night)
 * A material a system shades through its own set-up (gpu/chain.js hooks: the ground, water; gpu/nodemat.js diffuseRGB:
 * people, coats; trees.js VegNodeMaterial: the trees) keeps three's way; so does a physical material (glass), and so
 * does any material outside the family (an own colour node, not the value table): nothing of the family to share, so its
 * text stays three's.
 */
export function surfaceOf(m) {
  if (!installed || !m?.isMeshStandardNodeMaterial || m.isMeshPhysicalNodeMaterial || m.isMeshPhysicalMaterial) return null;
  if (ownSetup(m, 'setupDiffuseColor') || ownSetup(m, 'setupNormal') || ownSetup(m, 'setupVariants') || m.fragmentNode || m.userData?.surface === false) return OUTSIDE;
  const colour = !m.colorNode && (!m.map || TEX_SLOT(m.map));
  const family = colour || m.userData?.surfTable === true;
  if (!family || EXTRA_MAPS.some((k) => m[k]) || (m.emissiveMap && m.emissiveMap !== m.map)) return OUTSIDE;
  const nm = m.normalMap;
  const normal = !m.normalNode && !m.bumpMap && (!nm || (TEX_SLOT(nm) && m.normalMapType === THREE.TangentSpaceNormalMap && nm.format !== THREE.RGFormat && nm.format !== THREE.RED_GREEN_RGTC2_Format && nm.format !== THREE.RG11_EAC_Format));
  const emissive = (!m.emissiveNode || !!tagOf(m.emissiveNode)) && (!m.emissiveMap || m.emissiveMap === m.map);
  return { colour, normal, emissive, family };
}
/** The material builds through the shared graph (any of its stages): WP5.1b gives its every object the sun's shadow,
 *  weighed per object (shadows.js), so a receiver and a non-receiver are one program. */
export const sharedSurface = (m) => { const s = surfaceOf(m); return !!(s?.colour || s?.normal || s?.emissive || s?.family); };
const keyOf = (s, m) => (s ? `|surf${+s.colour}${+s.normal}${+s.emissive}${+s.family}${FAMILY.baked ? 'b' : ''}${FAMILY.room ? 'r' : ''}${FAMILY.sparkle ? 's' : ''}${m[SURF_SNOW] ? 'n' : ''}${m[SURF_SPARKLE] ? 'k' : ''}${m.userData?.dlSnow === true ? 'd' : ''}` : '');

// ---- per-object values (one node each, every material): the material's and geometry's choices, read at each draw
/** A texture slot read from the material, the white picture where it has none (three's own material references). */
class SlotTexture extends THREE.MaterialReferenceNode {
  static get type() { return 'SlotTexture'; }
  getValueFromReference(m = this.reference) { const t = m?.[this.property]; return TEX_SLOT(t) ? t : WHITE; }
  // never merged with another binding of the same picture (three merges texture nodes by the texture's uuid: a material
  // whose slots all hold the white picture would get one binding where one with maps gets three, another text)
  setNodeType(type) { super.setNodeType(type); this.node.mergeable = false; }
}
const MAP = new SlotTexture('map', 'texture'), NORMAL_MAP = new SlotTexture('normalMap', 'texture');
const geo = (o) => o?.geometry?.attributes || {};
const glowMasked = (m) => !!m?.emissiveNode?.surfGlow;
/** x vertex colours on (2: with their own alpha), y the glow varying on, z the glow without it (1, or 0 for a glow-masked part with no mask:
 *  three reads a missing attribute as 0), w the baked light on this geometry. */
const OBJ = uniform(new THREE.Vector4()).onObjectUpdate(({ object, material: m }, n) => {
  const a = geo(object), g = glowMasked(m);
  return n.value.set(m?.vertexColors && a.color ? (a.color.itemSize === 4 ? 2 : 1) : 0, g && a.glow ? 1 : 0, g && !a.glow ? 0 : 1, FAMILY.baked && a.bakedUv ? 1 : 0);
});
/** x normal map on, y emission x the colour map's texel, z night self-light (gpu/nodemat.js nightFill's k), w snow tint. */
const MAT = uniform(new THREE.Vector4()).onObjectUpdate(({ material: m }, n) => n.value.set(
  TEX_SLOT(m?.normalMap) ? 1 : 0, m?.emissiveMap && m.emissiveMap === m.map ? 1 : 0, m?.emissiveNode?.surfNight?.value ?? 0, m?.[SURF_SNOW] ? 1 : 0));
/** x sparkle strength, y room on, z room gain, w the daylight's snow fill (lazy/daylight.js). */
const MAT2 = uniform(new THREE.Vector4()).onObjectUpdate(({ material: m }, n) => {
  const r = m?.emissiveNode?.surfRoom;
  return n.value.set(m?.[SURF_SPARKLE] ?? 0, r ? 1 : 0, r ? r.value : 0, m?.userData?.dlSnow === true ? 1 : 0); // (dlSnow: in userData, kept by a clone as before)
});
/** The daylight's snow fill on this material (1 = snow): lazy/daylight.js multiplies the sky light by it. */
export const snowFillOn = MAT2.w;

/** A node whose build depends on the material and geometry being built (the vertex stage's attribute or constant). */
class PickNode extends THREE.Node {
  static get type() { return 'SurfacePick'; }
  constructor(type, pick) { super(type); this.pick = pick; }
  setup(builder) { return this.pick(builder); }
}
const has = (b, k) => b.geometry?.hasAttribute?.(k) === true;
const V_UV = varying(new PickNode('vec2', (b) => (has(b, 'uv') ? attribute('uv', 'vec2') : vec2(0))), 'vSurfUv');
const V_COL = varying(new PickNode('vec4', (b) => (b.material?.vertexColors && has(b, 'color') ? vec4(vertexColor()) : vec4(1))), 'vSurfCol');
const V_GLOW = varying(new PickNode('float', (b) => (has(b, 'glow') ? attribute('glow', 'float') : float(1))), 'vSurfGlow');
const GRID = 4096; // lazy/bakedlight.js: one float a vertex, u + v x 4096 on a 4096 grid
const V_BAKED = varying(new PickNode('vec2', (b) => {
  if (!has(b, 'bakedUv')) return vec2(0);
  const p = attribute('bakedUv', 'float'), iv = floor(p.div(GRID)).toVar('surfBakedV'); // (named: an unnamed one would take a number the fragment's names share)
  return vec2(p.sub(iv.mul(GRID)), iv).div(GRID - 1);
}), 'vBakedUv');

// ---- the shared terms
// (the alpha: three reads an RGB colour attribute as alpha exactly 1.0, never an interpolated 1, so only an RGBA one reads it)
const SURF_COLOR = vec4(materialReference('color', 'color'), 1).mul(MAP).mul(select(OBJ.x.greaterThan(0.5), vec4(V_COL.rgb, select(OBJ.x.greaterThan(1.5), V_COL.a, float(1))), vec4(1)));
const SURF_NORMAL = Fn(() => {
  const n = vec3(normalView).toVar('surfN');
  If(MAT.x.greaterThan(0.5), () => { n.assign(normalMap(NORMAL_MAP, materialReference('normalScale', 'vec2'))); });
  return n;
});
/** The room behind a window pane (materials.js roomWindow): ray-traced per pixel in the facade frame (see there). */
const winHash = (n) => fract(sin(n.mul(127.1)).mul(43758.5453));
export const roomRadiance = Fn(() => {
  // the window's world position and normal from the model matrix alone (before instancing), in the vertex stage
  const winPos = varying(modelWorldMatrix.mul(vec4(positionGeometry, 1.0)).xyz);
  const Nw0 = varying(modelWorldMatrix.mul(vec4(normalGeometry, 0.0)).xyz);
  const Tc = vec3(Nw0.z, 0.0, Nw0.x.negate());
  const tl = length(Tc);
  const room = vec3(0.04, 0.025, 0.015).toVar();
  If(tl.greaterThan(0.3).and(length(Nw0).greaterThan(0.1)), () => {
    const Nw = normalize(Nw0), T = Tc.div(tl);
    const V = normalize(winPos.sub(cameraPosition));
    const rd = vec3(dot(V, T), V.y, max(dot(V, Nw).negate(), 0.02)).toVar();
    const vUv = uv();
    const plain = vUv.y.greaterThan(50.0).toVar(); // Kit.room(.., plain): no source for what is inside, so no room is drawn
    const ro = vec3(vUv.x, select(plain, vUv.y.sub(100.0), vUv.y), 0.0).toVar();
    const W = 4.0, D = 5.0, C = 3.0;
    const x0 = floor(ro.x.div(W)).mul(W).toVar();
    const tb = float(D).div(rd.z).toVar();
    const tx = select(abs(rd.x).greaterThan(1e-4), select(rd.x.greaterThan(0.0), x0.add(W), x0).sub(ro.x).div(rd.x), 1e4).toVar();
    const ty0 = select(abs(rd.y).greaterThan(1e-4), select(rd.y.greaterThan(0.0), float(C), float(0.0)).sub(ro.y).div(rd.y), 1e4);
    const ty = select(ty0.lessThan(0.0), float(1e4), ty0).toVar();
    const t = min(tb, min(tx, ty)).toVar();
    // which surface the ray hits first (ordered compares, not t == ty / t == tb: exact on every backend)
    // (TSL: every value the branches below share is a .toVar() here, else its first branch alone would compute it)
    const hitY = ty.lessThanEqual(tb).and(ty.lessThanEqual(tx)).toVar(), hitB = hitY.not().and(tb.lessThanEqual(tx)).toVar();
    const p = ro.add(rd.mul(t)).toVar();
    const id = winHash(x0.mul(0.173).add(3.1)).toVar();
    const hy = clamp(p.y.div(C), 0.0, 1.0).toVar();
    const lamp = vec3(x0.add(id.mul(0.3).add(0.35).mul(W)), C - 0.5, D * 0.55);
    const dl = p.sub(lamp);
    const pool = exp(dot(dl, dl).mul(-0.35)).toVar();
    If(hitY.and(rd.y.greaterThan(0.0)), () => {
      room.assign(vec3(1.0, 0.54, 0.2).mul(pool.mul(0.9).add(0.5))); // ceiling
    }).ElseIf(hitY, () => {
      room.assign(vec3(0.85, 0.42, 0.14).mul(pool.mul(0.6).add(0.65))); // floor
    }).Else(() => {
      const side = select(hitB, float(1.0), float(0.72)); // back / side walls
      room.assign(vec3(0.95, 0.45, 0.13).mul(side).mul(mix(0.32, 1.0, smoothstep(0.05, 0.95, hy)).add(pool.mul(0.7))));
      const furn = step(fract(p.x.mul(0.43).add(p.z.mul(0.31)).add(id.mul(7.0))), 0.55).mul(smoothstep(0.22, 0.3, hy).oneMinus());
      room.mulAssign(furn.mul(0.4).oneMinus()); // tables, chairs
    });
    room.mulAssign(id.mul(0.36).add(0.82).mul(0.78).div(t.mul(0.05).add(1.0)));
    If(plain, () => {
      // lit, but nothing claimed: brighter towards the lamps under the ceiling, a slow drift along the wall
      const gy = smoothstep(-0.1, 1.0, ro.y.div(C)), gx = sin(ro.x.mul(0.55).add(1.3)).mul(0.5).add(0.5);
      room.assign(vec3(1.0, 0.46, 0.13).mul(gy.mul(0.28).add(0.24)).mul(gx.mul(0.12).add(0.92)));
    });
  });
  return room;
});
/** The emission: the material's emissive x intensity x the colour map's texel (where it is the emission map) x the glow
 *  mask, then the room (x room x gain) and the night self-light (+ albedo x the night's light x k), each in its If. */
const surfEmissive = (room) => Fn(() => {
  const e = vec3(vec4(materialReference('emissive', 'color').mul(materialReference('emissiveIntensity', 'float')), 1)
    .mul(select(MAT.y.greaterThan(0.5), MAP, vec4(1))).mul(select(OBJ.y.greaterThan(0.5), V_GLOW, OBJ.z))).toVar('surfE');
  if (room) If(MAT2.y.greaterThan(0.5), () => { e.assign(roomRadiance().mul(e).mul(MAT2.z)); });
  If(MAT.z.greaterThan(0), () => { e.addAssign(max(diffuseColor.rgb, 0).mul(NIGHT_LIGHT_FILL).mul(MAT.z)); });
  return e;
})();
let EMISSIVE = null, EMISSIVE_ROOM = null; // built once per kind (the room's term only on a map with rooms)

/** The baked light into a lighting model's light (lazy/bakedlight.js's term): R = sky seen (indirect light), G = the
 *  building's own room light (direct diffuse x the room glow now). Before the cast light, as it was. */
function bakedTerm(rl, dc) {
  If(OBJ.w.greaterThan(0.5), () => {
    const t = BAKED.map.sample(V_BAKED).toVar('blT');
    const vis = mix(float(1), t.r.mul(t.r), BAKED.sky.mul(BAKED.on)).toVar('blSky'); // R: sky seen
    rl.indirectDiffuse.mulAssign(vis);
    rl.indirectSpecular.mulAssign(vis);
    const room = pow(max(t.g.mul(t.g), 0), BAKED.falloff).mul(BAKED.room).mul(BAKED.on); // G: the room's own light
    rl.directDiffuse.addAssign(dc.rgb.mul(vec3(BAKED.glow)).mul(room));
  });
}
/** The snow's shade tint (WS-4, materials.js addSnowShading): shaded snow picks up a little of the sky colour. */
function snowTerm(rl, out, dc) {
  If(MAT.w.greaterThan(0.5), () => {
    const lit = dot(rl.directDiffuse, vec3(0.3333));
    out.addAssign(SNOW_TINT.mul(dc.rgb).mul(smoothstep(0.05, 0.5, lit).oneMinus()));
  });
}
/** Soft snow sparkle (WS-37, materials.js addSparkle): world-space cells that glint as the view moves (see there). */
function sparkleTerm(rl, out) {
  If(MAT2.x.greaterThan(0), () => {
    const cell = floor(positionWorld.mul(14.0));
    const h = fract(sin(dot(cell, vec3(12.9898, 78.233, 37.719))).mul(43758.5453));
    const vd = floor(normalize(positionView.negate()).mul(7.0)); // the direction to the eye, in 7 steps
    const tw = fract(h.mul(91.7).add(dot(vd, vec3(0.131, 0.217, 0.373))));
    const fade = smoothstep(0.012, 0.05, length(fwidth(positionWorld))).oneMinus();
    const lum = dot(rl.directDiffuse.add(rl.indirectDiffuse), vec3(0.3333));
    const s = step(0.992, h).mul(smoothstep(0.75, 1.0, tw)).mul(fade).mul(MAT2.x);
    out.addAssign(vec3(s.mul(min(lum.mul(1.6), 0.9))));
  });
}

// ---- the patch on three's standard node material (once; a physical one and every own set-up keep three's way)
let installed = false;
/** Installs the shared surface on THREE.MeshStandardNodeMaterial (idempotent). Before any material is hooked
 *  (gpu/lighting.js wraps the class's lighting model it finds): materials.js calls it as it loads. */
export function sharedSurfaces() {
  if (installed || !usable('MeshStandardNodeMaterial stages')) return; // (gone: three's own way, every caller's old hook)
  installed = true;
  const P = THREE.MeshStandardNodeMaterial.prototype;
  const base = { setup: P.setup, diffuse: P.setupDiffuseColor, normal: P.setupNormal, lighting: P.setupLighting, model: P.setupLightingModel, key: P.customProgramCacheKey };
  // (non-enumerable, as a class's own methods: three's NodeMaterial.copy copies every enumerable property onto a clone)
  const def = (k, value) => Object.defineProperty(P, k, { configurable: true, writable: true, enumerable: false, value });
  def('customProgramCacheKey', function () { return `${base.key.call(this)}${keyOf(surfaceOf(this), this)}`; });
  def('setup', function (builder) {
    if (!sharedSurface(this)) return base.setup.call(this, builder);
    FAMILY.frozen = true;
    // every uv read of the fragment stage (the colour map, the normal map's frame) through one varying, which the vertex
    // stage fills from the geometry's uv or with 0: the same text with or without a uv attribute. (The builder's own
    // context, for its whole build: the nodes are set up after this returns.)
    const ctx = builder.context, prev = ctx.getAttribute;
    ctx.getAttribute = (name, b) => (name === 'uv' && b.shaderStage === 'fragment' ? V_UV : prev ? prev(name, b) : null);
    return base.setup.call(this, builder);
  });
  def('setupDiffuseColor', function (builder) {
    const s = surfaceOf(this);
    if (!s?.colour || builder.geometry?.morphAttributes?.color) return base.diffuse.call(this, builder);
    const keep = [this.colorNode, this.vertexColors];
    this.colorNode = SURF_COLOR; this.vertexColors = false; // (the vertex colour is in SURF_COLOR; three's own pass stays for opacity, alpha test, instance colours)
    try { base.diffuse.call(this, builder); } finally { [this.colorNode, this.vertexColors] = keep; }
  });
  def('setupNormal', function (builder) {
    const s = surfaceOf(this);
    return s?.normal ? SURF_NORMAL() : base.normal.call(this, builder);
  });
  def('setupLighting', function (builder) {
    const s = surfaceOf(this);
    if (!s?.emissive || !s.family) return base.lighting.call(this, builder);
    const keep = this.emissiveNode;
    this.emissiveNode = FAMILY.room ? (EMISSIVE_ROOM ||= surfEmissive(true)) : (EMISSIVE ||= surfEmissive(false));
    try { return base.lighting.call(this, builder); } finally { this.emissiveNode = keep; }
  });
  def('setupLightingModel', function (builder) {
    const model = base.model.call(this, builder);
    const s = surfaceOf(this);
    if (!model || !s) return model;
    const baked = s.family && FAMILY.baked, snow = s.family, sparkle = s.family && FAMILY.sparkle; // (outside the family: the old hooks, materials.js)
    if (!baked && !snow && !sparkle) return model;
    const start = model.start, finish = model.finish;
    model.start = function (b) { start.call(this, b); if (baked) bakedTerm(b.context.reflectedLight, diffuseColor); };
    model.finish = function (b) {
      finish.call(this, b);
      if (snow) snowTerm(b.context.reflectedLight, b.context.outgoingLight, diffuseColor);
      if (sparkle) sparkleTerm(b.context.reflectedLight, b.context.outgoingLight);
    };
    return model;
  });
}
sharedSurfaces();
